import React, { useEffect } from "react";
import { applySeoOverride } from "../hooks/useSeo";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useJobModal } from "../context/JobModalContext";

const GROUPS = [
  {
    title: "Membership & pricing",
    items: [
      { q: "How does the free trial work?", a: "Post your first job and try SimplySitter free for one month. After the trial it's just $15.99/month, and you can cancel anytime before renewal at no charge." },
      { q: "When will I be charged?", a: "You won't be charged during your 1-month free trial. Billing of $15.99/month begins only after the trial ends, and we'll always remind you beforehand." },
      { q: "Can I cancel anytime?", a: "Yes. You can cancel your membership at any time from your account settings — no notice period and no cancellation fees." },
      { q: "Is there a placement commission?", a: "Never. You only pay the transparent monthly membership — we don't take a cut of what you pay your caregiver." },
    ],
  },
  {
    title: "Trust & safety",
    items: [
      { q: "Are the caregivers really vetted?", a: "Yes. Every caregiver verifies their identity, is personally interviewed, and passes background and reference checks before they can apply to your job." },
      { q: "What does the background check cover?", a: "We confirm identity documents, review work references and run a background screening. Caregivers can only apply once our team has approved their profile." },
      { q: "Who can see my job and contact details?", a: "Only vetted, approved caregivers can apply to your job. Your contact details are shared with a caregiver when you choose to move forward." },
    ],
  },
  {
    title: "For families",
    items: [
      { q: "How do I post a job?", a: "Tap “Post a job free”, describe what you need (service, city, schedule), create your account and activate your membership. Your job goes live after a quick review by our team." },
      { q: "How fast will I receive applications?", a: "Most families receive their first applications within 24–48 hours of their job going live." },
      { q: "Can I post more than one job?", a: "Absolutely. Your membership lets you post multiple jobs across different services and locations at no extra cost." },
    ],
  },
  {
    title: "For caregivers",
    items: [
      { q: "Is it free to join as a caregiver?", a: "Yes, joining as a caregiver is 100% free — no fees, ever." },
      { q: "Why do I need to be approved before applying?", a: "To keep families safe, our team reviews your ID and resume before you can apply. You'll be notified by email as soon as you're approved." },
      { q: "How do I apply to jobs?", a: "Once approved, browse open jobs in your area from your dashboard and apply in a few clicks. Families then contact you directly." },
    ],
  },
];

export default function FAQPage() {
  const { openJobModal } = useJobModal();
  useEffect(() => {
    document.title = "FAQ — Frequently Asked Questions | SimplySitter";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.setAttribute("content", "Answers to common questions about SimplySitter — free trial, pricing, vetting, background checks, posting jobs and applying as a caregiver.");
    applySeoOverride();
    return () => { document.title = "SimplySitter"; };
  }, []);

  return (
    <div>
      <Header />
      <section className="hero-gradient">
        <div className="max-w-4xl mx-auto px-5 pt-16 pb-12 text-center">
          <h1 className="font-display text-[2.6rem] leading-[1.05] sm:text-6xl text-[#1f1a1b]">Frequently asked questions</h1>
          <p className="mt-5 text-[18px] leading-relaxed text-[#5a5254] max-w-2xl mx-auto">
            Everything you need to know about SimplySitter — from the free trial to how we keep families safe.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-5 py-14 space-y-10">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <h2 className="font-display text-2xl text-[#1f1a1b] mb-3">{g.title}</h2>
              <Accordion type="single" collapsible className="w-full">
                {g.items.map((f, i) => (
                  <AccordionItem key={i} value={`${g.title}-${i}`} className="border border-black/[0.08] rounded-2xl mb-3 px-5">
                    <AccordionTrigger className="text-left font-display text-lg text-[#1f1a1b] hover:no-underline py-5">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[15px] text-[#5a5254] leading-relaxed pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#fdfaf7]">
        <div className="max-w-7xl mx-auto px-5 pb-16">
          <div className="rounded-3xl bg-[#7c2233] px-8 py-12 text-center">
            <h2 className="font-display text-3xl text-white">Still have questions?</h2>
            <p className="text-white/80 mt-3">Our team is happy to help. Reach us anytime.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href="mailto:hello@simplysitter.us" className="inline-flex items-center gap-2 rounded-xl bg-white text-[#7c2233] font-semibold px-7 py-3.5 hover:bg-[#f7e4e6] transition-colors"><Mail className="w-4 h-4" /> hello@simplysitter.us</a>
              <button onClick={() => openJobModal({})} className="rounded-xl border border-white/25 text-white font-medium px-7 py-3.5 hover:bg-white/10 transition-colors">Post a job free</button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
